"use client";

import { useState, useEffect } from "react";
import { CircleCheck as CheckCircle2, Send, Loader, Phone, Mail, MapPin, Clock, ShieldCheck, Globe } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { EmailLink } from "@/components/common/EmailLink";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
const assurances = ["Strict non-disclosure & confidential rate data handling", "Tailored for independent hotels, boutiques & regional chains", "Zero disruption to live PMS & channel distribution"];
const initialValues = {
  full_name: "",
  email_address: "",
  phone_number: "",
  case_type: "",
  brief_description: "",
  preferred_contact: "Email",
  confidentiality_acknowledged: false
};
function ConsultationForm() {
  const [formDef, setFormDef] = useState<any>(null);
  const [fetchTime, setFetchTime] = useState<number>(Date.now());
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [hpValue, setHpValue] = useState("");
  useEffect(() => {
    const client = getWvcClient();
    if (client?.pluginRequest) {
      client.pluginRequest("wvc-forms/v1/forms/contact-consultation-request").then((data: any) => {
        setFormDef(data);
        setFetchTime(Date.now());
      }).catch(() => {
        // Silent catch for initial definition fetch
      });
    }
  }, []);
  const validate = () => {
    const errs: Record<string, string> = {};
    if (!values.full_name.trim()) {
      errs.full_name = "Please share your full name";
    }
    if (!values.email_address.trim()) {
      errs.email_address = "Work email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email_address)) {
      errs.email_address = "Please enter a valid email address";
    }
    if (!values.phone_number.trim()) {
      errs.phone_number = "Phone number is required";
    }
    if (!values.case_type) {
      errs.case_type = "Please select a service";
    }
    if (!values.brief_description.trim()) {
      errs.brief_description = "Please provide a brief description of your property and goals";
    } else if (values.brief_description.trim().length < 20) {
      errs.brief_description = "Minimum 20 characters required";
    } else if (values.brief_description.trim().length > 500) {
      errs.brief_description = "Maximum 500 characters allowed";
    }
    if (!values.preferred_contact) {
      errs.preferred_contact = "Please select a contact preference";
    }
    if (!values.confidentiality_acknowledged) {
      errs.confidentiality_acknowledged = "Please acknowledge confidentiality to proceed";
    }
    return errs;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    const minSeconds = formDef?.spam?.min_seconds || 3;
    const elapsed = (Date.now() - fetchTime) / 1000;
    if (elapsed < minSeconds) {
      await new Promise(resolve => setTimeout(resolve, Math.ceil((minSeconds - elapsed) * 1000)));
    }
    setIsSubmitting(true);
    try {
      const client = getWvcClient();
      if (!client?.pluginRequest) {
        throw new Error("Form submission service unavailable");
      }
      const payload: any = {
        values: {
          full_name: values.full_name,
          email_address: values.email_address,
          phone_number: values.phone_number,
          case_type: values.case_type,
          brief_description: values.brief_description,
          preferred_contact: values.preferred_contact,
          confidentiality_acknowledged: values.confidentiality_acknowledged
        },
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef?.spam?.token) {
        payload.token = formDef.spam.token;
      }
      if (formDef?.spam?.honeypot?.field) {
        payload.hp = {
          [formDef.spam.honeypot.field]: hpValue
        };
      }
      const res = await client.pluginRequest("wvc-forms/v1/forms/contact-consultation-request/submit", {
        method: "POST",
        data: payload
      });
      if (res && res.success) {
        setIsSubmitted(true);
        const rawMessage = res.confirmation?.message || "";
        const cleanMessage = rawMessage ? rawMessage.replace(/<[^>]*>/g, "") : "Your consultation request has been received. Sunil Singh will review your distribution profile and reach out within 24 business hours.";
        setSuccessMessage(cleanMessage);
      } else if (res && res.code === "frms_validation_failed" && res.errors) {
        const serverErrs: Record<string, string> = {};
        Object.keys(res.errors).forEach(key => {
          if (res.errors[key]?.message) {
            serverErrs[key] = res.errors[key].message;
          }
        });
        setErrors(serverErrs);
        setSubmitError(res.message || "Please check the highlighted fields.");
      } else {
        setSubmitError(res?.message || "We could not submit your request at this moment. Please call our direct desk or try again.");
      }
    } catch (err: any) {
      setSubmitError(err?.message || "We could not submit your request at this moment. Please call our direct desk or try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
    setIsSubmitted(false);
    setSubmitError(null);
  };
  if (isSubmitted && !submitError) {
    return <div className="bg-white p-8 sm:p-10 rounded-2xl border border-border shadow-xl">
        <div className="flex flex-col items-center text-center gap-6 py-8">
          <div className="flex items-center justify-center size-16 rounded-xl bg-secondary/15 border border-secondary/40">
            <CheckCircle2 className="size-8 text-secondary" aria-label="Success check icon" />
          </div>
          <div className="space-y-2">
            <h3 className="font-sans font-bold text-2xl md:text-3xl text-foreground">
              Consultation Request <span className="text-secondary italic">Received.</span>
            </h3>
            <p className="font-default text-base text-muted-foreground max-w-md mx-auto leading-relaxed">
              {successMessage}
            </p>
          </div>
          <Button onClick={resetForm} variant="outline" className="mt-4 border-secondary text-secondary hover:bg-secondary hover:text-primary font-semibold px-6 py-2 rounded-full transition-colors">
            Submit Another Inquiry
          </Button>
        </div>
      </div>;
  }
  return <div className="bg-white p-6 sm:p-8 md:p-10 rounded-2xl border border-border shadow-xl">
      <div className="mb-6 border-b border-border pb-5">
        <div className="flex items-center gap-2 text-secondary text-xs uppercase tracking-widest font-mono font-medium mb-1">
          <ShieldCheck className="size-4" />
          <span>Confidential Assessment</span>
        </div>
        <h3 className="font-sans font-bold text-xl sm:text-2xl text-foreground">
          Request a Revenue Audit & Strategy Session
        </h3>
        <p className="font-default text-xs sm:text-sm text-muted-foreground mt-1">
          Complete the details below for a bespoke appraisal of your hotel's distribution and RevPAR potential.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <input type="hidden" name="@type" value="ContactPoint" />

        {formDef?.spam?.honeypot?.field && <div className="absolute -left-[9999px]" aria-hidden="true">
            <input type="text" name={formDef.spam.honeypot.field} tabIndex={-1} autoComplete="off" value={hpValue} onChange={e => setHpValue(e.target.value)} />
          </div>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label htmlFor="full_name" className="font-sans font-semibold text-xs uppercase tracking-wider text-foreground">
              Full Name <span className="text-secondary">*</span>
            </Label>
            <Input id="full_name" placeholder="e.g. Vikramaditya Singhania" value={values.full_name} onChange={e => setValues({
            ...values,
            full_name: e.target.value
          })} className="h-11 bg-white border-border text-foreground placeholder:text-muted-foreground rounded-lg focus-visible:border-secondary focus-visible:ring-secondary/30 transition-colors font-default text-sm" />
            {errors.full_name && <p className="text-destructive text-xs font-default mt-1">{errors.full_name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email_address" className="font-sans font-semibold text-xs uppercase tracking-wider text-foreground">
              Work Email <span className="text-secondary">*</span>
            </Label>
            <Input id="email_address" type="email" placeholder="e.g. gm@luxuryresortgoa.com" value={values.email_address} onChange={e => setValues({
            ...values,
            email_address: e.target.value
          })} className="h-11 bg-white border-border text-foreground placeholder:text-muted-foreground rounded-lg focus-visible:border-secondary focus-visible:ring-secondary/30 transition-colors font-default text-sm" />
            {errors.email_address && <p className="text-destructive text-xs font-default mt-1">{errors.email_address}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label htmlFor="phone_number" className="font-sans font-semibold text-xs uppercase tracking-wider text-foreground">
              Phone Number <span className="text-secondary">*</span>
            </Label>
            <Input id="phone_number" type="tel" placeholder="e.g. +91 98200 12345" value={values.phone_number} onChange={e => setValues({
            ...values,
            phone_number: e.target.value
          })} className="h-11 bg-white border-border text-foreground placeholder:text-muted-foreground rounded-lg focus-visible:border-secondary focus-visible:ring-secondary/30 transition-colors font-default text-sm" />
            {errors.phone_number && <p className="text-destructive text-xs font-default mt-1">{errors.phone_number}</p>}
          </div>

          <div className="space-y-2">
            <Label className="font-sans font-semibold text-xs uppercase tracking-wider text-foreground">
              Primary Focus Area <span className="text-secondary">*</span>
            </Label>
            <Select value={values.case_type} onValueChange={val => setValues({
            ...values,
            case_type: val
          })}>
              <SelectTrigger className="h-11 bg-white border-border text-foreground rounded-lg focus-visible:border-secondary focus-visible:ring-secondary/30 transition-colors font-default text-sm w-full">
                <SelectValue placeholder="Select consultation pillar" />
              </SelectTrigger>
              <SelectContent className="bg-white border-border text-foreground">
                <SelectItem value="OTA Management">OTA Revenue Optimization & Parity</SelectItem>
                <SelectItem value="Tech Implementation">Hospitality Tech & RMS Implementation</SelectItem>
                <SelectItem value="Revenue Audit">Comprehensive Hotel Revenue Audit</SelectItem>
                <SelectItem value="Portfolio Advisory">Multi-Property Advisory & Training</SelectItem>
                <SelectItem value="Other">Other Specific Requirement</SelectItem>
              </SelectContent>
            </Select>
            {errors.case_type && <p className="text-destructive text-xs font-default mt-1">{errors.case_type}</p>}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="brief_description" className="font-sans font-semibold text-xs uppercase tracking-wider text-foreground">
            Property Profile & Revenue Challenges <span className="text-secondary">*</span>
          </Label>
          <Textarea id="brief_description" rows={4} placeholder="Mention your room inventory, city/location, current PMS/channel manager, and top revenue bottlenecks..." value={values.brief_description} onChange={e => setValues({
          ...values,
          brief_description: e.target.value
        })} className="bg-white border-border text-foreground placeholder:text-muted-foreground rounded-lg focus-visible:border-secondary focus-visible:ring-secondary/30 transition-colors font-default text-sm resize-none" />
          {errors.brief_description && <p className="text-destructive text-xs font-default mt-1">{errors.brief_description}</p>}
        </div>

        <div className="space-y-2">
          <Label className="font-sans font-semibold text-xs uppercase tracking-wider text-foreground">
            Preferred Callback Channel
          </Label>
          <RadioGroup value={values.preferred_contact} onValueChange={val => setValues({
          ...values,
          preferred_contact: val
        })} className="flex flex-row gap-6 pt-1">
            <div className="flex items-center gap-2.5" data-index={0}>
              <RadioGroupItem value="Email" id="contact-email" className="size-4 border-secondary text-secondary focus-visible:ring-secondary" />
              <Label htmlFor="contact-email" className="font-default text-sm text-foreground cursor-pointer">
                Email Briefing
              </Label>
            </div>
            <div className="flex items-center gap-2.5" data-index={1}>
              <RadioGroupItem value="Phone" id="contact-phone" className="size-4 border-secondary text-secondary focus-visible:ring-secondary" />
              <Label htmlFor="contact-phone" className="font-default text-sm text-foreground cursor-pointer">
                Direct Phone Call
              </Label>
            </div>
          </RadioGroup>
          {errors.preferred_contact && <p className="text-destructive text-xs font-default mt-1">{errors.preferred_contact}</p>}
        </div>

        <Separator className="bg-border" />

        <div className="space-y-2">
          <div className="flex items-start gap-3">
            <Checkbox id="confidentiality_acknowledged" checked={values.confidentiality_acknowledged} onCheckedChange={checked => setValues({
            ...values,
            confidentiality_acknowledged: Boolean(checked)
          })} className="mt-0.5 border-secondary data-[state=checked]:bg-secondary data-[state=checked]:text-primary" />
            <Label htmlFor="confidentiality_acknowledged" className="font-default text-xs leading-relaxed text-muted-foreground cursor-pointer">
              I understand this inquiry is confidential and initiates a private discovery review with Sunil Singh & the HotelRevBaBa advisory team.
            </Label>
          </div>
          {errors.confidentiality_acknowledged && <p className="text-destructive text-xs font-default mt-1">{errors.confidentiality_acknowledged}</p>}
        </div>

        {submitError && <div className="p-3.5 border border-destructive/40 rounded-lg bg-destructive/15 text-destructive-foreground">
            <p className="font-default text-xs sm:text-sm">{submitError}</p>
          </div>}

        <div className="space-y-3 pt-2">
          <Button type="submit" disabled={isSubmitting} className="w-full h-12 sm:h-13 font-sans font-bold text-sm sm:text-base bg-secondary text-primary hover:bg-secondary/90 shadow-md rounded-full transition-all">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4 text-primary" aria-label="Loading spinner" />
                Processing Inquiry...
              </> : <>
                <Send className="size-4 mr-2 text-primary" aria-label="Send inquiry icon" />
                Submit Consultation Request
              </>}
          </Button>
          <p className="font-default italic text-xs text-muted-foreground text-center">
            Zero spam. Commercial rate data encrypted and handled under institutional NDA standards.
          </p>
        </div>
      </form>
    </div>;
}
export default function StartYourRevenueJourney() {
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: contextRef,
    isVisible: contextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="46" id="start-your-revenue-journey" className="bg-white text-foreground py-20 sm:py-24 md:py-28 lg:py-32 px-4 sm:px-6 md:px-10 lg:px-16 relative overflow-hidden">
      {/* Background ambient lighting */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-secondary/5 blur-[120px] pointer-events-none rounded-lg" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-secondary/5 blur-[140px] pointer-events-none rounded-lg" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-16 items-start">
          {/* Left Column: Form housed in rounded-2xl white card (7 cols desktop, 2nd on mobile) */}
          <div ref={formRef} className={`lg:col-span-7 order-2 lg:order-1 transition-all duration-700 ease-out ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <ConsultationForm />
          </div>

          {/* Right Column: Context, H2, Blockquote, Channels (5 cols desktop, 1st on mobile) */}
          <div ref={contextRef} className={`lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-28 transition-all duration-700 delay-100 ease-out ${contextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="space-y-6">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary font-semibold mb-3">
                  — Direct Advisory
                </p>
                <h2 className="font-sans font-bold text-3xl sm:text-4xl lg:text-[42px] leading-[1.15] text-foreground tracking-tight">
                  Start Your <br className="hidden sm:inline" />
                  <span className="text-secondary italic">Revenue Journey</span>
                </h2>
              </div>

              <p className="font-default text-base sm:text-[17px] leading-relaxed text-muted-foreground">
                Partner with Sunil Singh to eliminate OTA margin bleed, resolve rate parity conflicts, and configure high-converting distribution tech across your hotel portfolio.
              </p>

              {/* AEO Gold-bordered blockquote */}
              <blockquote className="border-l-4 border-secondary bg-secondary/10 px-5 py-4 rounded-md italic font-medium text-foreground text-sm sm:text-base my-6 shadow-sm">
                "Our partner properties consistently recover 15% to 28% in uncaptured room revenue within their initial 90-day strategy deployment."
              </blockquote>

              {/* Contact channels with gold-accented icons */}
              <div className="space-y-4 pt-2">
                <h3 className="font-sans font-semibold text-xs uppercase tracking-widest text-muted-foreground">
                  Direct Contact Channels
                </h3>
                <div className="grid grid-cols-1 gap-3.5">
                  <div data-index={0} className="p-3.5 sm:p-4 rounded-xl bg-card border border-border flex items-start gap-3.5 hover:border-secondary/40 transition-colors">
                    <div className="p-2.5 rounded-lg bg-secondary/15 border border-secondary/30 shrink-0 mt-0.5">
                      <Phone className="size-4 text-secondary" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5">
                        Direct Consultation Line
                      </p>
                      <a href="tel:+917972195929" className="font-sans font-semibold text-sm sm:text-base text-foreground hover:text-secondary transition-colors inline-block">
                        +91 79721 95929
                      </a>
                      <p className="text-xs text-muted-foreground mt-0.5 font-default">
                        Mon – Sat, 9:00 AM – 7:00 PM IST
                      </p>
                    </div>
                  </div>

                  <div data-index={1} className="p-3.5 sm:p-4 rounded-xl bg-card border border-border flex items-start gap-3.5 hover:border-secondary/40 transition-colors">
                    <div className="p-2.5 rounded-lg bg-secondary/15 border border-secondary/30 shrink-0 mt-0.5">
                      <Mail className="size-4 text-secondary" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5">
                        Inquiry & Audit Desk
                      </p>
                      <EmailLink user="sunil.singh" domain="hotelrevbaba.com" className="font-sans font-semibold text-sm sm:text-base text-foreground hover:text-secondary transition-colors inline-block">
                        sunil.singh@hotelrevbaba.com
                      </EmailLink>
                      <p className="text-xs text-muted-foreground mt-0.5 font-default">
                        Guaranteed reply within 24 business hours
                      </p>
                    </div>
                  </div>

                  <div data-index={2} className="p-3.5 sm:p-4 rounded-xl bg-card border border-border flex items-start gap-3.5 hover:border-secondary/40 transition-colors">
                    <div className="p-2.5 rounded-lg bg-secondary/15 border border-secondary/30 shrink-0 mt-0.5">
                      <MapPin className="size-4 text-secondary" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5">
                        Primary Practice Hub
                      </p>
                      <p className="font-sans font-semibold text-sm sm:text-base text-foreground">
                        C-1202, Vaidehi Hills, Karadva Road, Dindoli, Surat, Gujarat 394210
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 font-default">
                        Serving independent hotels & chains across India
                      </p>
                    </div>
                  </div>

                  <div data-index={3} className="p-3.5 sm:p-4 rounded-xl bg-card border border-border flex items-start gap-3.5 hover:border-secondary/40 transition-colors">
                    <div className="p-2.5 rounded-lg bg-secondary/15 border border-secondary/30 shrink-0 mt-0.5">
                      <Clock className="size-4 text-secondary" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5">
                        Rapid Revenue Audit
                      </p>
                      <p className="font-sans font-semibold text-sm sm:text-base text-foreground">
                        1-Business-Day Turnaround
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 font-default">
                        Complimentary initial rate parity & OTA check
                      </p>
                    </div>
                  </div>

                  <div data-index={4} className="p-3.5 sm:p-4 rounded-xl bg-card border border-border hover:border-secondary/40 transition-colors space-y-3">
                    <div className="flex items-start gap-3.5">
                      <div className="p-2.5 rounded-lg bg-secondary/15 border border-secondary/30 shrink-0 mt-0.5">
                        <Globe className="size-4 text-secondary" aria-hidden="true" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5">
                          Find Us Online
                        </p>
                        <p className="text-xs text-muted-foreground font-default">
                          See reviews and directions on Google
                        </p>
                      </div>
                    </div>
                    <div className="w-full h-64 sm:h-80 rounded-xl overflow-hidden border border-border">
                      <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3057.499431845912!2d72.86782120000001!3d21.1392362!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be0515d376b8565%3A0xa8c130b719cdc2f2!2sHotelRevBaBa!5e1!3m2!1sen!2sin!4v1791452004712!5m2!1sen!2sin" width="100%" height="100%" style={{
                      border: 0
                    }} allowFullScreen={true} loading="lazy" referrerPolicy="strict-origin-when-cross-origin" title="HotelRevBaBa Location Map" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Assurances list */}
              <div className="pt-2">
                <ul className="space-y-2.5">
                  {assurances.map((item, i) => <li key={i} data-index={i} className="flex items-center gap-2.5 text-xs sm:text-sm text-muted-foreground">
                      <span className="size-1.5 rounded-lg bg-secondary shrink-0" />
                      <span>{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}